<script setup lang="ts">
import type { VerticalNavigationLink } from '#ui/types'
import ConfirmDialog from '../ConfirmDialog.vue'
import { DeleteWorkspaceDocument, GetDashboardsDocument, GetWorkspacesDocument } from './Workspace.generated'

const props = defineProps<{
  workspaceId: string
}>()

const { canChangeOrgConfig } = useCurrentUser()
const { enabled: areBehaviorTreesEnabled } = useFlag('BEHAVIOR_TREES')
const { enabled: areSleuthIssuesEnabled } = useFlag('SLEUTH_ISSUES')
const { enabled: pulseFeatures } = useFlag('PULSE_FEATURES')
const openDashboards = ref(false)

const route = useRoute()

const { data: workspaceData } = useQuery({
  query: GetWorkspacesDocument,
})

const { data: dashboardData } = useQuery({
  query: GetDashboardsDocument,
})

const workspace = computed(() => {
  const nodes = workspaceData.value?.workspaces?.edges?.map(e => e.node) ?? []
  return nodes.find(w => w.id === props.workspaceId)
})

const dashboards = computed(() => {
  return dashboardData.value?.workspaces.nodes.find(ws => ws.id === props.workspaceId)?.dashboards.nodes ?? []
})

function isProcessesActive() {
  const path = route.path
  return path.startsWith(`/teamspace/${props.workspaceId}/trees`)
}

const onRoute = (routeNames: string[]) => routeNames.includes(route.name as string)

function getSubLinks() {
  const links: (VerticalNavigationLink & { showConfigWarning?: boolean })[] = pulseFeatures.value
    ? [
        { label: 'Reviews', to: `/teamspace/${props.workspaceId}/reviews`, icon: 'solid:file' },
        { label: 'Surveys', to: `/teamspace/${props.workspaceId}/surveys`, icon: 'solid:clipboard-check' },
      ]
    : []

  if (areBehaviorTreesEnabled.value) {
    links.push({
      label: 'Processes',
      to: `/teamspace/${props.workspaceId}/trees`,
      icon: 'solid:share-nodes',
      exact: false,
      active: isProcessesActive(),
    })
  }

  if (areSleuthIssuesEnabled.value) {
    const issuesTeamConfigured = workspace.value?.issuesTeamConfigured ?? false
    const settingsLink = `/settings/teamspace/${props.workspaceId}/issues`
    const actualLink = `/teamspace/${props.workspaceId}/issues`

    links.push(
      {
        label: 'Specs',
        to: issuesTeamConfigured ? `${actualLink}/specs` : settingsLink,
        icon: 'solid:folder-gear',
        active: onRoute([
          'teamspace-workspaceId-issues-specs',
          'teamspace-workspaceId-issues-specs-hash',
        ]),
        showConfigWarning: !issuesTeamConfigured,
      },
      {
        label: 'Task Board',
        to: issuesTeamConfigured ? `${actualLink}` : settingsLink,
        icon: 'solid:chart-kanban',
        active: onRoute([
          'teamspace-workspaceId-issues',
          'teamspace-workspaceId-issues-sessions-id',
        ]),
        showConfigWarning: !issuesTeamConfigured,
      },
      {
        label: 'Pull Requests',
        to: issuesTeamConfigured ? `${actualLink}/pull-requests` : settingsLink,
        icon: 'solid:code-pull-request',
        active: onRoute([
          'teamspace-workspaceId-issues-pullRequests',
          'teamspace-workspaceId-issues-pullRequests-id',
        ]),
        showConfigWarning: !issuesTeamConfigured,
      },
    )
  }

  return links
}

function onWorkspaceHome() {
  return route.name === 'teamspace-workspaceId'
}

function onDashboardPage() {
  return route.name === 'teamspace-workspaceId-dashboards-dashboardId'
}

const { execute: deleteWorkspace } = useMutation(DeleteWorkspaceDocument, { refetchTags: ['workspaces'] })

const confirmDialog = ref<InstanceType<typeof ConfirmDialog>>()
const toBeDeletedWorkspaceName = ref('')

const isLastWorkspace = computed(() => {
  const nodes = workspaceData.value?.workspaces?.edges?.map(e => e.node) ?? []
  const nonGlobalWorkspaces = nodes.filter(w => !w.isGlobal)
  return nonGlobalWorkspaces.length === 1
})

async function maybeDeleteWorkspace() {
  if (!workspace.value)
    return

  toBeDeletedWorkspaceName.value = workspace.value.name
  const { isCanceled } = await confirmDialog.value!.reveal()
  if (isCanceled)
    return

  const response = await deleteWorkspace({ id: props.workspaceId })
  const error = handleMutationError({ combinedError: response.error, errors: response.data?.deleteWorkspace?.errors })
  if (!error) {
    localStorage.removeItem('last-workspace')
    await navigateTo({ name: 'index' })
  }
}

function dropdownItems() {
  return [
    [
      {
        label: 'Teamspace settings',
        icon: 'light:gear',
        click: () => navigateTo({
          name: 'settings-teamspace-teamspaceId',
          params: { teamspaceId: props.workspaceId },
        }),
      },
      {
        label: 'Delete',
        icon: 'light:trash-can',
        disabled: isLastWorkspace.value,
        click: () => { maybeDeleteWorkspace() },
      },
    ],
  ]
}
</script>

<template>
  <ConfirmDialog ref="confirmDialog" is-delete>
    <div>Are you sure you want to delete '{{ toBeDeletedWorkspaceName }}' teamspace?</div>
  </ConfirmDialog>

  <div v-if="workspace" class="flex flex-col px-2">
    <UButton
      color="white-nav"
      variant="ghost"
      active-class="dark:bg-gray-800 bg-gray-200"
      class="group/navItem relative mb-px flex flex-1 justify-between px-2"
      :class="onWorkspaceHome() ? ['bg-blue-80', 'dark:bg-gray-900'] : []"
      @click="navigateTo({ name: 'teamspace-workspaceId', params: { workspaceId } })"
    >
      <div v-if="onWorkspaceHome()" class="absolute inset-y-1.5 left-[-5px] w-[3px] rounded-md bg-blue-600" />
      <UTooltip
        :text="workspace.name"
        :prevent="workspace.name.length < 23"
        :popper="{ placement: 'top', offsetDistance: 14 }"
        :open-delay="500"
        class="flex flex-1 items-center gap-x-2 truncate"
      >
        <WorkspaceIcon v-if="workspace.marker" :marker="workspace.marker" class="shrink-0 grow-0" />
        <span class="truncate text-left text-nav font-light text-gray-900 dark:text-white">
          {{ workspace.name }}
        </span>
      </UTooltip>
      <OptionsDropdown
        v-if="canChangeOrgConfig"
        :popper="{ offsetDistance: 12, offsetSkid: 6 }"
        :items="dropdownItems()"
        :ui="{ width: 'w-48' }"
        name="org"
        @click.stop.prevent
      >
        <OptionsButton
          size="xs"
          color="nav"
          class="invisible group-hover/navItem:visible"
          data-testid="org-dropdown-button"
        />
      </OptionsDropdown>
    </UButton>

    <div class="flex flex-col">
      <UPopover
        v-if="dashboards.length > 0 && pulseFeatures"
        v-model:open="openDashboards"
        :popper="{ placement: 'bottom-start', offsetDistance: -36, offsetSkid: 212 }"
        class="pl-4"
      >
        <UButton
          color="white-nav"
          size="md"
          variant="ghost"
          class="group relative flex w-full items-center justify-start px-2 font-light"
          :class="onDashboardPage() || openDashboards ? ['bg-blue-80', 'dark:bg-gray-900', 'dark:text-white'] : []"
        >
          <UIcon
            name="solid:square-poll-vertical"
            class="!size-4 shrink-0 grow-0 group-hover:text-gray-700 dark:group-hover:text-gray-200"
            :class="onDashboardPage() || openDashboards ? ['text-gray-700', 'dark:text-gray-200'] : ['text-gray-400', 'dark:text-gray-500']"
          />
          <div v-if="onDashboardPage()" class="absolute inset-y-1.5 left-[-5px] w-[3px] rounded-md bg-blue-600" />
          <div class="-ml-0.5 text-nav">
            Dashboards
          </div>
          <UIcon
            name="solid:caret-right"
            class="ml-auto shrink-0 grow-0 text-sm text-gray-600 transition-transform duration-200 dark:text-gray-400"
            :class="{ 'rotate-180': openDashboards }"
          />
        </UButton>
        <template #panel="{ close }">
          <div class="flex flex-col gap-px bg-blue-50 p-1 text-gray-900 dark:bg-gray-950 dark:text-gray-400  ">
            <NuxtLink
              v-for="dashboard in dashboards"
              :key="dashboard.id"
              :to="{
                name: 'teamspace-workspaceId-dashboards-dashboardId',
                params: { workspaceId, dashboardId: dashboard.id },
              }"
              class="rounded-md p-2 opacity-75 hover:bg-blue-80 hover:opacity-100 dark:hover:bg-gray-900 dark:hover:text-white"
              @click="close"
            >
              <div class="flex items-center gap-x-2">
                <UIcon :name="getIconFromDashboardName(dashboard.name)" />
                <div>{{ dashboard.name }}</div>
              </div>
            </NuxtLink>
          </div>
        </template>
      </UPopover>
      <UVerticalNavigation
        :links="getSubLinks()"
        class="pl-4"
        :ui="{ padding: 'p-2', size: '!text-nav', font: 'font-light', icon: { base: 'flex-shrink-0 size-4 relative' } }"
      >
        <template #default="{ link, isActive }">
          <div v-if="isActive" class="absolute inset-y-1.5 left-[-5px] w-[3px] rounded-md bg-blue-600" />
          <UTooltip
            text="You must configure AI planning before accessing this page."
            :prevent="!link.showConfigWarning"
            :popper="{ placement: 'top', offsetDistance: 14 }"
            class="flex flex-1 items-center gap-x-2 truncate"
          >
            <span class="relative truncate">{{ link.label }}</span>
          </UTooltip>
        </template>
      </UVerticalNavigation>
    </div>
  </div>
</template>
